import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export const priorityOptions = [
  { value: 0, label: 'Low', color: 'green', hex: '#1f883d' },
  { value: 1, label: 'Medium', color: 'blue', hex: '#0969da' },
  { value: 2, label: 'High', color: 'orange', hex: '#bc4c00' },
  { value: 3, label: 'Urgent', color: 'red', hex: '#cf222e' },
];

export const priorityLabelColor = (priority) => {
  const opt = priorityOptions.find(p => p.value === priority);
  return opt || priorityOptions[0];
};

export const isStatusClosed = (statusName) => {
  if (!statusName) return false;
  return /done|closed|completed|cancel/i.test(statusName);
};

const TOKEN_KEYS = ['is', 'label', 'assignee', 'author', 'type', 'priority', 'sort', 'project', 'status', 'no'];

export const parseIssuesQuery = (raw) => {
  const result = {
    text: '',
    is: [],
    label: [],
    assignee: [],
    author: [],
    type: [],
    priority: [],
    status: [],
    no: [],
    sort: null,
    project: [],
  };
  if (!raw) return result;

  const textParts = [];
  // Handle, in order of preference: field:"quoted value", field:value, "quoted text", word.
  const regex = /([a-zA-Z][\w-]*):"([^"]+)"|([a-zA-Z][\w-]*):(\S+)|"([^"]+)"|(\S+)/g;
  let match;
  while ((match = regex.exec(raw)) !== null) {
    const key = (match[1] || match[3] || '').toLowerCase();
    const val = match[2] ?? match[4];
    const freeQuoted = match[5];
    const freeWord = match[6];
    if (key) {
      const cleanVal = (val || '').replace(/^"/, '').replace(/"$/, '');
      if (TOKEN_KEYS.includes(key) && cleanVal) {
        if (key === 'sort') {
          result.sort = cleanVal.toLowerCase();
        } else {
          result[key].push(cleanVal.toLowerCase());
        }
        continue;
      }
      textParts.push(`${key}:${cleanVal}`);
      continue;
    }
    if (freeQuoted !== undefined) { textParts.push(freeQuoted); continue; }
    if (freeWord !== undefined) { textParts.push(freeWord); continue; }
  }
  result.text = textParts.join(' ').toLowerCase().trim();
  return result;
};

const matchesTokenList = (tokens, candidates) => {
  if (!tokens.length) return true;
  const lowered = candidates.filter(Boolean).map(c => String(c).toLowerCase());
  return tokens.every(tok => lowered.some(c => c.includes(tok)));
};

// Server-side filter params for the task list, derived from the query box.
export const queryToServerParams = (raw) => {
  const p = parseIssuesQuery(raw || '');
  const prioToken = p.priority[0];
  const prioOpt = prioToken
    ? priorityOptions.find(o => o.label.toLowerCase() === prioToken)
    : null;
  return {
    search: p.text || '',
    status_name: p.status[0] || '',
    task_type_name: p.type[0] || '',
    priority: prioOpt ? String(prioOpt.value) : '',
    assignee_name: p.assignee[0] || '',
  };
};

export const filterTasks = (tasks, query) => {
  const parsed = typeof query === 'string' ? parseIssuesQuery(query) : query;

  let filtered = tasks.filter(task => {
    // is:open / is:closed
    const closed = isStatusClosed(task.status_name);
    if (parsed.is.includes('open') && closed) return false;
    if (parsed.is.includes('closed') && !closed) return false;

    // free text: search title, task_code, description (HTML-stripped),
    // status/type/project names, and assignee names — match if ALL
    // whitespace-separated terms appear somewhere (AND semantics).
    if (parsed.text) {
      const stripped = (task.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ');
      const assigneeHay = (task.task_members || [])
        .flatMap(m => [m.user_name, m.employee_id])
        .filter(Boolean)
        .join(' ');
      const hay = [
        task.title,
        task.task_code,
        stripped,
        task.status_name,
        task.task_type_name,
        task.project_name,
        task.project_code,
        assigneeHay,
      ].filter(Boolean).join(' ').toLowerCase();
      const terms = parsed.text.split(/\s+/).filter(Boolean);
      if (!terms.every(t => hay.includes(t))) return false;
    }

    // label: matches task_type_name OR priority label
    if (parsed.label.length) {
      const prio = priorityLabelColor(task.priority)?.label || '';
      const candidates = [task.task_type_name, prio];
      if (!matchesTokenList(parsed.label, candidates)) return false;
    }

    // status: filter by status name
    if (parsed.status.length && !matchesTokenList(parsed.status, [task.status_name])) return false;

    // no: fields that should be empty / unassigned
    if (parsed.no.length) {
      for (const key of parsed.no) {
        if (key === 'status' && task.status) return false;
        if (key === 'type' && task.task_type) return false;
        if (key === 'priority' && task.priority != null && task.priority !== undefined && task.priority !== '') return false;
        if (key === 'assignee' && (task.task_members || []).length > 0) return false;
      }
    }

    if (parsed.type.length && !matchesTokenList(parsed.type, [task.task_type_name])) return false;

    if (parsed.priority.length) {
      const prio = priorityLabelColor(task.priority)?.label || '';
      if (!matchesTokenList(parsed.priority, [prio])) return false;
    }

    if (parsed.assignee.length) {
      const candidates = (task.task_members || []).flatMap(m => [m.user_name, m.employee_id]).filter(Boolean);
      if (!matchesTokenList(parsed.assignee, candidates)) return false;
    }

    if (parsed.author.length) {
      const names = [task.created_by_name, task.created_by];
      if (!matchesTokenList(parsed.author, names)) return false;
    }

    if (parsed.project.length) {
      const names = [task.project_code, task.project_name];
      if (!matchesTokenList(parsed.project, names)) return false;
    }

    return true;
  });

  // Sort
  const sort = parsed.sort;
  if (sort) {
    const dir = sort.endsWith('-asc') ? 1 : -1;
    if (sort.startsWith('created')) {
      filtered = [...filtered].sort((a, b) => dir * (dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()));
    } else if (sort.startsWith('updated')) {
      filtered = [...filtered].sort((a, b) => dir * (dayjs(a.updated_at || a.created_at).valueOf() - dayjs(b.updated_at || b.created_at).valueOf()));
    } else if (sort.startsWith('priority')) {
      filtered = [...filtered].sort((a, b) => dir * ((a.priority || 0) - (b.priority || 0)));
    } else if (sort.startsWith('due')) {
      filtered = [...filtered].sort((a, b) => {
        const aV = a.due_date ? dayjs(a.due_date).valueOf() : Infinity;
        const bV = b.due_date ? dayjs(b.due_date).valueOf() : Infinity;
        return dir * (aV - bV);
      });
    }
  } else {
    // default newest first
    filtered = [...filtered].sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf());
  }

  return filtered;
};

// String-manipulation helpers: rebuild query strings when side-dropdowns change.
const stripToken = (raw, key, value) => {
  const needle = value ? `${key}:${value}` : `${key}:`;
  return raw
    .split(/\s+/)
    .filter(tok => value ? tok.toLowerCase() !== needle.toLowerCase() : !tok.toLowerCase().startsWith(needle.toLowerCase()))
    .join(' ')
    .trim();
};

export const setUniqueToken = (raw, key, value) => {
  const stripped = stripToken(raw || '', key);
  const suffix = value ? `${key}:${quoteIfNeeded(value)}` : '';
  return [stripped, suffix].filter(Boolean).join(' ').trim();
};

export const toggleToken = (raw, key, value) => {
  const token = `${key}:${quoteIfNeeded(value)}`;
  const parts = (raw || '').split(/\s+/).filter(Boolean);
  const idx = parts.findIndex(p => p.toLowerCase() === token.toLowerCase());
  if (idx >= 0) parts.splice(idx, 1);
  else parts.push(token);
  return parts.join(' ').trim();
};

const quoteIfNeeded = (v) => (/\s/.test(v) ? `"${v}"` : v);

export const timeAgo = (dt) => (dt ? dayjs(dt).fromNow() : '');
